import { AbstractDocument, Identifiable, Timestampable, Untrackable } from '../../../abstract/Document';
import { Places } from '../../Places';
import { Suppliers } from '../../Suppliers';
import type { ISupplier } from '../../Suppliers.types';
import type { IAddonMultiCounty as IAddon, IAddon_Definition } from './multiCounty.types';
import { arrayRemove, arrayUnion, runTransaction, serverTimestamp } from 'firebase/firestore';

@Identifiable
@Timestampable
@Untrackable
export class AddonMultiCounty extends AbstractDocument<IAddon> {
  readonly collections = {};

  /**
   * Returns the `Place` that contains this `Addon`.
   */
  get place() {
    return Places._.getById(this.reference.parent.parent!.id);
  }

  /**
   * Returns whether the addon can be claimed by the provided supplier [sub-]type.
   *
   * @param type The supplier type, or `_` to target all types.
   * @param subtype The supplier sub-type, or `_` to target all sub-types.
   * @param snapshot The `Addon` snapshot, optional.
   */
  async isAvailable<T extends keyof IAddon['suppliers']>(type?: T, subtype?: string, snapshot?: IAddon) {
    try {
      if (snapshot == null) {
        snapshot = await this.get(true);
      }

      const definition = await this.getDefinition(type, subtype, snapshot);

      if (definition == null) {
        return false;
      }

      return definition.limit > (definition.suppliers ?? []).length;
    } catch (e) {
      return false;
    }
  }

  /**
   * Returns the most specific addon definition for the provided supplier [sub-]type.
   *
   * @param type The supplier type, or `_` to target all types.
   * @param subtype The supplier sub-type, or `_` to target all sub-types.
   * @param snapshot The `Addon` snapshot, optional.
   */
  async getDefinition<T extends keyof IAddon['suppliers']>(type?: T, subtype?: string, snapshot?: IAddon) {
    if (snapshot == null) {
      snapshot = await this.get(true);
    }

    if (snapshot == null) {
      throw new Error(`Addon '${this.id}' doesn't exist for place '${this.place.id}'.`);
    }

    const { suppliers } = snapshot;

    if (type == null) {
      type = '_' as T;
    }

    if (subtype == null) {
      subtype = '_';
    }

    /**
     * If we have T = venue, and S = castle, we want to return the most specific definition that's
     * available:
     *
     * 1. [T][S]
     * 2. [T][_]
     * 3. [_][_]
     */
    return (suppliers?.[type]?.[subtype] ?? suppliers?.[type]?.['_'] ?? suppliers?.['_']?.['_']) as IAddon_Definition;
  }

  /**
   * Subscribes the provided supplier to this specific addon.
   *
   * @param supplier `ISupplier` to subscribe.
   * @param subtype Whether to target the specific supplier sub-type or not.
   */
  async subscribe(supplier: ISupplier, subtype: string | boolean = false) {
    /**
     * The supplier type.
     */
    let type: keyof IAddon['suppliers'] = supplier.type?.[0] ?? '_';

    /**
     * The supplier sub-type.
     */
    if (subtype !== false) {
      if (typeof subtype !== 'string') {
        // @ts-ignore - Not all supplier types have sub-types.
        subtype = supplier.typeDetails?.[type]?.type?.[0] ?? '_';
      }
    } else {
      subtype = '_';
    }

    return await runTransaction(this.reference.firestore, async (transaction) => {
      const addon = (await transaction.get(this.reference)).data();

      if (addon == null) {
        throw new Error(`Addon '${this.id}' doesn't exist for place '${this.place.id}'.`);
      }

      if (addon.suppliers[type] == null) {
        type = '_';
        subtype = '_';
      } else if (addon.suppliers[type]?.[subtype.toString()] == null) {
        subtype = '_';
      }

      const [available, area] = await Promise.all([this.isAvailable(type, subtype.toString(), addon), this.place.getPlaceName()]);

      if (available !== true) {
        if (addon.suppliers[type]?.[subtype.toString()].suppliers?.includes(supplier.id) !== true) {
          throw new Error(
            `Addon '${this.id}' is unavailable for place '${this.place.id}' (${area}), supplier type '${type}' and subtype '${subtype}'.`,
          );
        }
      }

      transaction.set(
        Suppliers._.getById(supplier.id).reference,
        {
          additionalAreas: arrayUnion(area),
          additionalAreasPending: arrayRemove(area),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      transaction.set(
        this.reference,
        {
          suppliers: {
            [type]: {
              [subtype.toString()]: {
                suppliers: arrayUnion(supplier.id),
              },
            },
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      return true;
    });
  }

  /**
   * Unsubscribes the provided supplier from this specific addon.
   *
   * @param supplier `ISupplier` to unsubscribe.
   * @param subtype Whether to target the specific supplier sub-type or not.
   */
  async unsubscribe(supplier: ISupplier, subtype: string | boolean = false) {
    /**
     * The supplier type.
     */
    let type: keyof IAddon['suppliers'] = supplier.type?.[0] ?? '_';

    /**
     * The supplier sub-type.
     */
    if (subtype !== false) {
      if (typeof subtype !== 'string') {
        // @ts-ignore - Not all supplier types have sub-types.
        subtype = supplier.typeDetails?.[type]?.type?.[0] ?? '_';
      }
    } else {
      subtype = '_';
    }

    return await runTransaction(this.reference.firestore, async (transaction) => {
      const addon = (await transaction.get(this.reference)).data();

      if (addon == null) {
        // Return silently if the addon doesn't exist for a place, it doesn't matter for unsubscribing
        return true;
      }

      if (addon.suppliers[type] == null) {
        type = '_';
        subtype = '_';
      } else if (addon.suppliers[type]?.[subtype.toString()] == null) {
        subtype = '_';
      }

      const area = await this.place.getPlaceName();

      transaction.set(
        Suppliers._.getById(supplier.id).reference,
        {
          additionalAreas: arrayRemove(area),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      transaction.set(
        this.reference,
        {
          suppliers: {
            [type]: {
              [subtype.toString()]: {
                suppliers: arrayRemove(supplier.id),
              },
            },
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      return true;
    });
  }
}
